import type { GroupWorkLocation } from '../../constants/groupWorkLocation';
import { expandDateRange, isWeekendDateString } from '../../utils/dateHelpers';
import { roundBwDecimal } from './bandwidthFormat';

/** Offshore teams: 9 hours per working day (Mon–Fri). */
export const BW_UTIL_HOURS_PER_DAY_OFFSHORE = 9;

/** Onshore/OnSite teams: 8 hours per working day (Mon–Fri). */
export const BW_UTIL_HOURS_PER_DAY_ONSHORE = 8;

/** @deprecated Prefer location-specific helpers; defaults to offshore (9h). */
export const BW_UTIL_HOURS_PER_DAY = BW_UTIL_HOURS_PER_DAY_OFFSHORE;

/**
 * `getBandwidthExportDateRange` often returns datetime-local values (`YYYY-MM-DDTHH:mm`).
 * Utilization uses calendar days only — take the date portion before `T` or space.
 */
export function normalizeBandwidthRangeDateToYmd(raw: string): string {
  const s = raw.trim();
  if (!s) return '';
  const head = s.includes('T') ? s.split('T')[0]! : s.split(' ')[0]!;
  return /^\d{4}-\d{2}-\d{2}$/.test(head) ? head : '';
}

export function normalizeBandwidthWorkLocation(value: string | undefined | null): GroupWorkLocation {
  return value === 'onshore_onsite' ? 'onshore_onsite' : 'offshore';
}

export function getBandwidthDailyHoursForWorkLocation(workLocation?: string | null): number {
  return normalizeBandwidthWorkLocation(workLocation) === 'onshore_onsite'
    ? BW_UTIL_HOURS_PER_DAY_ONSHORE
    : BW_UTIL_HOURS_PER_DAY_OFFSHORE;
}

/**
 * Count Mon–Fri days in [ymdFrom, ymdTo] inclusive (UTC calendar). Weekends are excluded.
 */
export function countBandwidthWorkingDaysInRange(ymdFrom: string, ymdTo: string): number {
  if (!ymdFrom || !ymdTo || ymdFrom > ymdTo) return 0;
  return expandDateRange(ymdFrom, ymdTo).filter((d) => !isWeekendDateString(d)).length;
}

/**
 * Expected available hours per person for the selected UTC calendar range,
 * based on team work location (8h onshore, 9h offshore per working day).
 */
export function getBandwidthCapacityHoursForWorkLocation(
  dateFrom: string,
  dateTo: string,
  workLocation?: string | null
): number {
  const from = normalizeBandwidthRangeDateToYmd(dateFrom);
  const to = normalizeBandwidthRangeDateToYmd(dateTo);
  if (!from || !to) return 0;
  const workingDays = countBandwidthWorkingDaysInRange(from, to);
  const dailyHours = getBandwidthDailyHoursForWorkLocation(workLocation);
  return roundBwDecimal(workingDays * dailyHours);
}

/** Alias for per-person capacity in a date range for a given work location. */
export function getBandwidthPerPersonCapacityHours(
  dateFrom: string,
  dateTo: string,
  workLocation?: string | null
): number {
  return getBandwidthCapacityHoursForWorkLocation(dateFrom, dateTo, workLocation);
}

export function getBandwidthGroupCapacityHours(
  dateFrom: string,
  dateTo: string,
  group: { workLocation?: string | null; users: unknown[] }
): number {
  const capPerPerson = getBandwidthCapacityHoursForWorkLocation(dateFrom, dateTo, group.workLocation);
  return roundBwDecimal(capPerPerson * Math.max(1, group.users.length));
}

export function getBandwidthUtilizationPct(loggedHours: number, capacityHours: number): number | null {
  if (!(capacityHours > 0)) return null;
  return roundBwDecimal((loggedHours / capacityHours) * 100);
}
